import React, { useState, useRef, useEffect } from 'react';
import { Ouvrage } from '../../1-bibliotheque-prix/data/ServicesData';
import { fuzzyMatchOuvrages } from '../utils/fuzzyMatch';

interface OuvrageSearchDropdownProps {
  ouvrages: Ouvrage[];
  selectedOuvrageId: number | null;
  onSelect: (ouvrage: Ouvrage | null) => void;
  onClose: () => void;
}

const OuvrageSearchDropdown: React.FC<OuvrageSearchDropdownProps> = ({
  ouvrages,
  selectedOuvrageId,
  onSelect,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const filteredOuvrages =
    query.trim().length > 0
      ? fuzzyMatchOuvrages(query, ouvrages).map((r) => r.ouvrage)
      : ouvrages;

  return (
    <div
      ref={containerRef}
      className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
    >
      <div className="p-2">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un ouvrage..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
        />
      </div>

      <ul className="max-h-60 overflow-y-auto py-1">
        {/* Option to dissociate */}
        <li>
          <button
            onClick={() => { onSelect(null); onClose(); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 italic"
          >
            Aucune correspondance
          </button>
        </li>

        {filteredOuvrages.map((ouvrage) => (
          <li key={ouvrage.id}>
            <button
              onClick={() => { onSelect(ouvrage); onClose(); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-violet-50 transition-colors ${
                ouvrage.id === selectedOuvrageId ? 'bg-violet-50 text-violet-700' : 'text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{ouvrage.intitule}</span>
                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 whitespace-nowrap">
                  {ouvrage.categorie.split(' > ').pop()}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {ouvrage.categorie}
                <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">
                  {ouvrage.unite}
                </span>
              </div>
            </button>
          </li>
        ))}

        {filteredOuvrages.length === 0 && (
          <li className="px-3 py-4 text-sm text-gray-400 text-center">
            Aucun ouvrage trouvé
          </li>
        )}
      </ul>
    </div>
  );
};

export default OuvrageSearchDropdown;
